import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography, Grid, useTheme, Divider } from "@mui/material";
import PaymentsIcon from "@mui/icons-material/Payments";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { RootState } from "../../../store/Index";
import { tokens } from "../../../theme";
import apiService from "../../../service/Api";
import Card from "../../../components/ui/Card";
import Button from "../../../components/ui/Button";
import CustomTextField from "../../../components/ui/Textfield";
import Chip from "../../../components/ui/Chip";
import Modal from "../../../components/ui/Modal";
import Skeleton from "../../../components/ui/Skeleton";
import { formatISODateTime } from "../../../utils/FormatDate";

const PRICING_CONFIG_URL = "api/chema/pricing-config/";

interface PaymentMethodConfig {
  key: string;
  label: string;
  feeVaries: boolean;
  processorFeePercent: number;
  processorFeeFlat: number;
  enabled: boolean;
}

interface PricingConfig {
  platformFeePercent: number;
  paymentMethods: PaymentMethodConfig[];
}

interface PaymentMethodResponse {
  key: string;
  label: string;
  fee_varies: boolean;
  processor_fee_percent: string | number;
  processor_fee_flat: string | number;
  enabled: boolean;
}

interface PricingConfigResponse {
  platform_fee_percent: string | number;
  payment_methods: PaymentMethodResponse[];
  last_updated_by_name: string | null;
  updated_at: string | null;
}


const toNumber = (value: string | number | null | undefined): number =>
  value === null || value === undefined || value === ""
    ? 0
    : typeof value === "number"
      ? value
      : Number.parseFloat(value) || 0;

const DEFAULT_CONFIG: PricingConfig = {
  platformFeePercent: 0,
  paymentMethods: [
    { key: "ecocash", label: "EcoCash", feeVaries: false, processorFeePercent: 0, processorFeeFlat: 0, enabled: false },
    { key: "paynow", label: "Paynow (OneMoney, InnBucks, Zipit)", feeVaries: false, processorFeePercent: 0, processorFeeFlat: 0, enabled: false },
    { key: "card", label: "Visa / Mastercard", feeVaries: false, processorFeePercent: 0, processorFeeFlat: 0, enabled: false },
    { key: "paypal", label: "PayPal", feeVaries: false, processorFeePercent: 0, processorFeeFlat: 0, enabled: false },
  ],
};

const fromResponse = (data: PricingConfigResponse): PricingConfig => {
  const methods = data.payment_methods ?? [];
  return {
    platformFeePercent: toNumber(data.platform_fee_percent),
    paymentMethods: methods.length
      ? methods.map((m) => ({
          key: m.key,
          label: m.label,
          feeVaries: Boolean(m.fee_varies),
          processorFeePercent: toNumber(m.processor_fee_percent),
          processorFeeFlat: toNumber(m.processor_fee_flat),
          enabled: Boolean(m.enabled),
        }))
      : DEFAULT_CONFIG.paymentMethods.map((m) => ({ ...m })),
  };
};

const toPayload = (config: PricingConfig) => ({
  platform_fee_percent: config.platformFeePercent,
  payment_methods: config.paymentMethods.map((m) => ({
    key: m.key,
    label: m.label,
    fee_varies: m.feeVaries,
    processor_fee_percent: m.feeVaries ? 0 : m.processorFeePercent,
    processor_fee_flat: m.feeVaries ? 0 : m.processorFeeFlat,
    enabled: m.enabled,
  })),
});

const readApiError = (err: unknown, fallback: string): string => {
  const res = (
    err as { response?: { status?: number; data?: { detail?: string } } }
  )?.response;
  if (res?.status === 403) return "You do not have permission to manage Chema pricing.";
  return res?.data?.detail || fallback;
};

const NumberInput: React.FC<{
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
}> = ({ label, value, onChange, min = 0, max }) => {
  const [text, setText] = useState(String(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(String(value));
  }, [value, focused]);

  const clamp = (n: number) => {
    let v = n;
    if (min !== undefined && v < min) v = min;
    if (max !== undefined && v > max) v = max;
    return v;
  };

  return (
    <CustomTextField
      label={label}
      type="text"
      inputProps={{ inputMode: "decimal" }}
      value={text}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;
        setText(raw);
        const n = Number.parseFloat(raw);
        if (Number.isFinite(n)) onChange(clamp(n));
      }}
      onBlur={() => {
        setFocused(false);
        const n = Number.parseFloat(text);
        const safe = clamp(Number.isFinite(n) ? n : 0);
        setText(String(safe));
        if (safe !== value) onChange(safe);
      }}
    />
  );
};


const ChemaPricingConfig: React.FC = () => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const [savedConfig, setSavedConfig] = useState<PricingConfig>(DEFAULT_CONFIG);
  const [draftConfig, setDraftConfig] = useState<PricingConfig>(DEFAULT_CONFIG);
  const [lastUpdatedBy, setLastUpdatedBy] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const isDirty = useMemo(
    () => JSON.stringify(draftConfig) !== JSON.stringify(savedConfig),
    [draftConfig, savedConfig],
  );

  const enabledMethodCount = useMemo(
    () => draftConfig.paymentMethods.filter((m) => m.enabled).length,
    [draftConfig],
  );

  const applyConfig = useCallback((config: PricingConfig) => {
    setSavedConfig(config);
    setDraftConfig(config);
  }, []);

  const fetchConfig = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await apiService.get<PricingConfigResponse>(PRICING_CONFIG_URL);
      applyConfig(fromResponse(response.data));
      setLastUpdatedBy(response.data.last_updated_by_name);
      setLastUpdatedAt(response.data.updated_at);
    } catch (err: unknown) {
      setLoadError(readApiError(err, "Failed to load Chema pricing configuration."));
    } finally {
      setIsLoading(false);
    }
  }, [applyConfig]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);


  const setField = <K extends keyof PricingConfig>(key: K, value: PricingConfig[K]) => {
    setDraftConfig((prev) => ({ ...prev, [key]: value }));
  };

  const setMethodField = (
    index: number,
    key: keyof PaymentMethodConfig,
    value: PaymentMethodConfig[keyof PaymentMethodConfig],
  ) => {
    setDraftConfig((prev) => ({
      ...prev,
      paymentMethods: prev.paymentMethods.map((m, i) =>
        i === index ? { ...m, [key]: value } : m,
      ),
    }));
  };

  const handleReset = () => applyConfig(savedConfig);

  const handleConfirmSave = async () => {
    setIsSaving(true);
    try {
      const response = await apiService.put<PricingConfigResponse>(
        PRICING_CONFIG_URL,
        toPayload(draftConfig),
      );
      applyConfig(fromResponse(response.data));
      setLastUpdatedBy(response.data.last_updated_by_name);
      setLastUpdatedAt(response.data.updated_at);
      toast.success("Chema pricing configuration saved. The public pricing page will reflect it shortly.");
      setConfirmOpen(false);
    } catch (err: unknown) {
      toast.error(readApiError(err, "Failed to save. Please try again."));
    } finally {
      setIsSaving(false);
    }
  };


  const labelColor = { color: colors.primary[300] } as const;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary[100], mb: 0.5 }}>
          Chema Pricing &amp; Fees
        </Typography>
        <Typography variant="body2" sx={labelColor}>
          The single source of truth for what the public pricing page shows and what the donation
          flow charges. Changes apply to every new donation.
        </Typography>
      </Box>

      {isLoading ? (
        <Grid container spacing={3}>
          {[0, 1].map((i) => (
            <Grid size={{ xs: 12, md: 6 }} key={i}>
              <Skeleton variant="rounded" height={220} />
            </Grid>
          ))}
        </Grid>
      ) : loadError ? (
        <Card>
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" sx={{ ...labelColor, mb: 2 }}>
              {loadError}
            </Typography>
            <Button label="Retry" onClick={fetchConfig} />
          </Box>
        </Card>
      ) : (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          sx={{ display: "flex", flexDirection: "column", gap: 3, pb: 12 }}
        >
    
          <Card
            title="Platform fee"
            subtitle="Chema's own cut, charged on every donation as a percentage."
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <NumberInput
                  label="Platform fee (%)"
                  value={draftConfig.platformFeePercent}
                  onChange={(v) => setField("platformFeePercent", v)}
                  max={100}
                />
              </Grid>
            </Grid>
          </Card>

          <Card
            title="Payment methods & processing fees"
            subtitle="Processor rates per method. Disable a method to hide it from the pricing page and the donate modal. Set a method to Varies when the fee is decided outside Chema."
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {draftConfig.paymentMethods.map((method, index) => (
                <Box
                  key={method.key}
                  sx={{
                    border: `1px solid ${colors.primary[300]}30`,
                    borderRadius: "12px",
                    p: 2,
                    opacity: method.enabled ? 1 : 0.55,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1.5,
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.primary[100] }}>
                      {method.label}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        label={method.feeVaries ? "Fee: Varies" : "Fee: Fixed rate"}
                        onClick={() => setMethodField(index, "feeVaries", !method.feeVaries)}
                        sx={{
                          backgroundColor: method.feeVaries ? colors.primary[400] : "transparent",
                          color: method.feeVaries ? colors.primary[900] : colors.primary[300],
                          border: `1.5px solid ${colors.primary[400]}`,
                          minWidth: "130px",
                        }}
                      />
                      <Button
                        label={method.enabled ? "Enabled" : "Disabled"}
                        onClick={() => setMethodField(index, "enabled", !method.enabled)}
                        sx={{
                          backgroundColor: method.enabled ? colors.primary[400] : "transparent",
                          color: method.enabled ? colors.primary[900] : colors.primary[300],
                          border: `1.5px solid ${colors.primary[400]}`,
                          minWidth: "110px",
                        }}
                      />
                    </Box>
                  </Box>

                  {method.feeVaries ? (
                    <Typography variant="caption" sx={{ ...labelColor, display: "block" }}>
                      Shows &quot;Varies&quot; on the pricing page and is excluded from the fee
                      calculator. Use this when the provider sets its own rate (e.g. PayPal per
                      country)
                    </Typography>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6, sm: 4 }}>
                        <NumberInput
                          label="Fee (%)"
                          value={method.processorFeePercent}
                          onChange={(v) =>
                            setMethodField(index, "processorFeePercent", v)
                          }
                          max={100}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, sm: 4 }}>
                        <NumberInput
                          label="Flat fee ($)"
                          value={method.processorFeeFlat}
                          onChange={(v) =>
                            setMethodField(index, "processorFeeFlat", v)
                          }
                        />
                      </Grid>
                    </Grid>
                  )}
                </Box>
              ))}
            </Box>
          </Card>

          <Card>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              <InfoOutlinedIcon fontSize="small" sx={{ ...labelColor, mt: 0.3 }} />
              <Typography variant="body2" sx={labelColor}>
                Processing rates here should match your Paynow merchant agreement. Paynow&apos;s
                published rates are at paynow.co.zw/Home/Fees, but negotiated rates can differ.
              </Typography>
            </Box>
          </Card>
        </Box>
      )}

       {!isLoading && !loadError && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            mt: -10,
            py: 2,
            px: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            backgroundColor: theme.palette.mode === "dark" ? colors.primary[900] : "#ffffff",
            borderTop: `1px solid ${colors.primary[300]}20`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Chip
              status={isDirty ? "warning" : "success"}
              label={isDirty ? "Unsaved changes" : "All changes saved"}
            />
            <Typography variant="caption" sx={labelColor}>
              {lastUpdatedBy
                ? `Last updated by ${lastUpdatedBy}${
                    lastUpdatedAt ? ` on ${formatISODateTime(lastUpdatedAt)}` : ""
                  }`
                : "Not configured yet"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              label="Reset"
              onClick={handleReset}
              disabled={!isDirty || isSaving}
              sx={{
                backgroundColor: "transparent",
                color: colors.primary[300],
                border: `1.5px solid ${colors.primary[300]}40`,
              }}
            />
            <Button
              label={isSaving ? "Saving..." : "Save changes"}
              onClick={() => setConfirmOpen(true)}
              disabled={!isDirty || isSaving}
            />
          </Box>
        </Box>
      )}

      <Modal
        open={confirmOpen}
        onClose={() => !isSaving && setConfirmOpen(false)}
        title="Apply pricing changes"
        actions={
          <>
            <Button
              label="Cancel"
              onClick={() => setConfirmOpen(false)}
              disabled={isSaving}
              sx={{
                backgroundColor: "transparent",
                color: colors.primary[300],
                border: `1.5px solid ${colors.primary[300]}40`,
              }}
            />
            <Button
              label={isSaving ? "Applying..." : "Apply"}
              onClick={handleConfirmSave}
              disabled={isSaving}
            />
          </>
        }
      >
        <Typography variant="body2" sx={labelColor}>
          These rates take effect for every new donation and update the public pricing page.
          Donations already in progress are not affected.
        </Typography>
        <Divider sx={{ my: 2, borderColor: `${colors.primary[300]}20` }} />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PaymentsIcon fontSize="small" sx={labelColor} />
            <Typography variant="caption" sx={labelColor}>
              Platform fee {draftConfig.platformFeePercent}%
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PaymentsIcon fontSize="small" sx={labelColor} />
            <Typography variant="caption" sx={labelColor}>
              {enabledMethodCount} payment method{enabledMethodCount === 1 ? "" : "s"} enabled
            </Typography>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ChemaPricingConfig;
